import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { TenantPublicConfig } from '@/types/TenantConfig'
import { getPack, DEFAULT_PACK } from '@/design/packs'
import { applyBrandValues, computeBrandValues } from '@/i18n/brandTokens'

/**
 * The tenant's public config (Tenant Config Model, P0) fetched before mount. Branding
 * consumers (logo, titles, legal links) read from here instead of hardcoded values.
 */
export const useTenantStore = defineStore('tenant', () => {
    const config = ref<TenantPublicConfig | null>(null)
    /** P1: module key → enabled (from the authenticated /tenant/config). Empty = all on. */
    const modules = ref<Record<string, boolean>>({})

    const setConfig = (c: TenantPublicConfig) => {
        config.value = c
        // Publish brand values so i18n re-resolves {{agencyName}}/{{agencyEmail}} tokens with the
        // tenant's real identity — covers the case where branding arrives after i18n messages load.
        applyBrandValues(computeBrandValues(c))
    }

    /** A module is enabled unless the tenant explicitly disabled it. */
    const moduleEnabled = (key?: string) => !key || modules.value[key] !== false

    /** Fetch the full (P0+P1) document after login; fail-soft. */
    const loadFullConfig = async () => {
        try {
            const res = await fetch('/api/v1/app/config', { credentials: 'include' })
            if (!res.ok) return
            const doc = await res.json() as TenantPublicConfig
            if (doc && typeof doc === 'object') {
                // Web-efektif modüller: çözülmüş yüzey bloğu; eski config'lerde köprü alan.
                const mods = doc.surfaces?.web?.modules ?? doc.modules
                if (mods) modules.value = mods
                config.value = { ...config.value, ...doc }
                // Post-login enrichment may add support email / identity — re-resolve brand tokens.
                applyBrandValues(computeBrandValues(config.value))
            }
        } catch { /* fail soft */ }
    }

    const appName = computed(() => config.value?.identity?.appName || 'TAP')
    const brandName = computed(() => config.value?.identity?.brandName || appName.value)
    const logoLight = computed(() => config.value?.identity?.logoLight || null)
    const logoDark = computed(() => config.value?.identity?.logoDark || null)
    const logoIcon = computed(() => config.value?.identity?.logoIcon || null)
    const tagline = computed(() => config.value?.identity?.tagline || null)
    const copyright = computed(() =>
        (config.value?.identity?.copyright || '').replace('{{year}}', String(new Date().getFullYear())) || null)
    // Design Pack (kilitli tasarım kimliği) — çözülmüş web bloğundan (köprü: appearance)
    const pack = computed(() =>
        getPack((config.value?.surfaces?.web?.appearance ?? config.value?.appearance)?.['pack']))
    const packLocked = computed(() => pack.value.id !== DEFAULT_PACK)
    const loginVariant = computed(() => pack.value.login)
    const dashVariant = computed(() => pack.value.dash)

    const legal = computed(() => config.value?.legal ?? {})
    const currency = computed(() => (config.value?.locale?.currency || 'TRY').toUpperCase())
    const urls = computed(() => config.value?.urls ?? {})

    return { config, modules, setConfig, moduleEnabled, loadFullConfig, pack, packLocked, loginVariant, dashVariant, appName, brandName, logoLight, logoDark, logoIcon, tagline, copyright, legal, urls, currency }
})
